import Logger from 'wanderu-logger';
import currencyService from 'wanderu-service-currency';

const logger = Logger.getLogger('I18nDataStore');

class I18nDataStore {
    constructor() {
        this.defaultLocale = undefined;
        this.defaultCurrency = undefined;
        this.locale = undefined;
        this.currency = undefined;
        this.translations = {};
        this.appConfigs = {};
        this.localeChangeListeners = {};
        this.currencyChangeListeners = {};
        this.localeToCurrencyMap = {};
    }

    toJson() {
        return {
            defaultLocale: this.getDefaultLocale(),
            locale: this.getLocale(),
            translations: this.getTranslations(),
            currency: this.getCurrency(),
            defaultCurrency: this.getDefaultCurrency(),
            currencyConversionRates: currencyService.getAllConversionRates(),
            supportedCurrencies: currencyService.getSupportedCurrencies(),
            localeToCurrencyMap: this.localeToCurrencyMap,
        };
    }

    getLocale() {
        return this.locale;
    }

    setLocale(locale) {
        this.locale = locale;
    }

    getDefaultLocale() {
        return this.defaultLocale;
    }

    setDefaultLocale(defaultLocale) {
        this.defaultLocale = defaultLocale;
    }

    getCurrency() {
        return this.currency;
    }

    setCurrency(currency) {
        this.currency = currency;
    }

    getDefaultCurrency(locale) {
        const currentLocale = locale || this.getLocale();
        return this.localeToCurrencyMap[currentLocale] || this.defaultCurrency;
    }

    setDefaultCurrency(defaultCurrency) {
        this.defaultCurrency = defaultCurrency;
    }

    setLocaleToCurrencyMap(localeToCurrencyMap) {
        this.localeToCurrencyMap = localeToCurrencyMap;
    }

    getTranslations() {
        return this.translations;
    }

    setTranslations(translations) {
        this.translations = translations;
    }

    setAppTranslations(locale, namespace, appTranslations) {
        const translations = this.getTranslations();
        if (!Object.prototype.hasOwnProperty.call(translations, locale)) {
            translations[locale] = {};
        }

        if (Object.prototype.hasOwnProperty.call(translations[locale], namespace)) {
            logger.info(
                'setAppTranslations',
                `Translations for namespace ${namespace} and locale ${locale} already `
                + 'exists! Overwriting.',
                {
                    locale,
                    namespace,
                }
            );
        }

        const newTranslations = {
            ...translations,
            [locale]: {
                ...translations[locale],
                [namespace]: appTranslations,
            },
        };

        this.setTranslations(newTranslations);
    }

    getAppConfigs() {
        return this.appConfigs;
    }

    getAppConfig(namespace) {
        return this.getAppConfigs()[namespace];
    }

    setAppConfigs(appConfigs) {
        Object.values(appConfigs).map(this._assertValidAppConfig);
        this.appConfigs = appConfigs;
    }

    addAppConfig(appConfig) {
        const appConfigs = this.getAppConfigs();
        this._assertValidAppConfig(appConfig);

        const { namespace } = appConfig;
        if (Object.prototype.hasOwnProperty.call(appConfigs, namespace)) {
            logger.info('registerTranslatableApp', `App with ${namespace} already registered!`, {
                namespace,
            });
        }

        const newAppConfigs = {
            ...appConfigs,
            [appConfig.namespace]: appConfig,
        };

        this.setAppConfigs(newAppConfigs);
    }

    removeAppConfig(namespace) {
        if (!Object.prototype.hasOwnProperty.call(this.appConfigs, namespace)) {
            throw new Error(`App with ${namespace} is not registered!`);
        }

        const {
            [namespace]: _removedConfig,
            ...newAppConfigs
        } = this.getAppConfigs();

        this.setAppConfigs(newAppConfigs);
    }

    setLocaleChangeListeners(localeChangeListeners) {
        this.localeChangeListeners = localeChangeListeners;
    }

    getLocaleChangeListeners() {
        return this.localeChangeListeners;
    }

    localeChangeListenerExists(handle) {
        const localeChangeListeners = this.getLocaleChangeListeners();
        return Object.prototype.hasOwnProperty.call(localeChangeListeners, handle);
    }

    addLocaleChangeListener(callback) {
        const localeChangeListeners = this.getLocaleChangeListeners();
        const index = Object.keys(localeChangeListeners).length;
        const handle = `${Date.now()}${index}`;

        // mutate for performance
        localeChangeListeners[handle] = callback;

        this.setLocaleChangeListeners(localeChangeListeners);
        return handle;
    }

    removeLocaleChangeListener(handle) {
        const localeChangeListeners = this.getLocaleChangeListeners();

        // mutate for performance
        delete localeChangeListeners[handle];

        this.setLocaleChangeListeners(localeChangeListeners);
    }

    setCurrencyChangeListeners(currencyChangeListeners) {
        this.currencyChangeListeners = currencyChangeListeners;
    }

    getCurrencyChangeListeners() {
        return this.currencyChangeListeners;
    }

    currencyChangeListenerExists(handle) {
        const currencyChangeListeners = this.getCurrencyChangeListeners();
        return Object.prototype.hasOwnProperty.call(currencyChangeListeners, handle);
    }

    addCurrencyChangeListener(callback) {
        const currencyChangeListeners = this.getCurrencyChangeListeners();
        const index = Object.keys(currencyChangeListeners).length;
        const handle = `${Date.now()}${index}`;

        // mutate for performance
        currencyChangeListeners[handle] = callback;

        this.setCurrencyChangeListeners(currencyChangeListeners);
        return handle;
    }

    removeCurrencyChangeListener(handle) {
        const currencyChangeListeners = this.getCurrencyChangeListeners();

        // mutate for performance
        delete currencyChangeListeners[handle];

        this.setCurrencyChangeListeners(currencyChangeListeners);
    }

    // eslint-disable-next-line class-methods-use-this
    _assertValidAppConfig(appConfig) {
        const {
            defaultLocale,
            namespace,
            supportedLocales,
            supportedCurrencies,
            translationFetchFn,
        } = appConfig;

        const messageStart = `${appConfig.namespace} must have`;

        if (!namespace || typeof namespace !== 'string') {
            throw new Error(`${messageStart} a namespace that is a string`);
        }

        if (!supportedLocales || !Array.isArray(supportedLocales)) {
            throw new Error(`${messageStart} an array of supportedLocales`);
        }

        if (!supportedCurrencies || !Array.isArray(supportedCurrencies)) {
            throw new Error(`${messageStart} an array of supportedCurrencies`);
        }

        if (!translationFetchFn) {
            throw new Error(`${messageStart} a translationFetchFn`);
        }

        if (defaultLocale && typeof defaultLocale !== 'string') {
            throw new Error(
                `${appConfig.namespace} if there is a defaultLocale, it must be a string`,
            );
        }
    }
}

export default I18nDataStore;
